import type { FieldProps } from 'formik';
import { ErrorMessage } from 'formik';
import type { ComponentPropsWithoutRef } from 'react';
import React from 'react';
import type { WikiPopoverProps } from '~/components/wiki/WikiPopover';
import { cn } from '~/utils/common';
import type { FormLabelProps } from './FormLabel';
import { FormLabel } from './FormLabel';

type InputValue = string | number | null;
type InputFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value'
> & { value?: InputValue };
const InputField: React.FC<InputFieldProps> = ({
  value,
  className,
  ...props
}) => (
  <input
    {...props}
    value={String(value ?? '')}
    className={cn('input', className)}
  />
);

type TextareaFieldProps = Omit<
  ComponentPropsWithoutRef<'textarea'>,
  'value'
> & { value: InputValue };
const TextareaField: React.FC<TextareaFieldProps> = ({
  value,
  className,
  ...props
}) => (
  <textarea
    {...props}
    value={value ?? ''}
    className={cn('textarea', className)}
  />
);

export type Props = FieldProps & {
  type?: 'text' | 'number' | 'email' | 'password' | 'textarea';
  helpText?: string;
  helpTextPlacement?: FormLabelProps['helpTextPlacement'];
  className?: string;
  isInvalid?: boolean;
  label?: React.ReactNode;
  renderInput?: (input: JSX.Element) => JSX.Element;
  required?: boolean;
  wikiPopoverId?: number;
  wikiPopoverProps?: WikiPopoverProps;
};

export const FormikTextField: React.FC<Props> = ({
  field,
  form,
  type = 'text',
  className,
  isInvalid = false,
  label,
  renderInput,
  helpText,
  helpTextPlacement,
  required = false,
  wikiPopoverId,
  wikiPopoverProps,
  ...props
}: Props) => {
  let FieldComponent: React.FC<any> = InputField;
  if (type === 'textarea') FieldComponent = TextareaField;

  const input = (
    <FieldComponent
      {...field}
      {...props}
      id={field.name}
      type={type}
      className={cn('w-full', className, {
        'input-error': isInvalid,
      })}
    />
  );

  return (
    <div className="form-control">
      <FormLabel
        name={field.name}
        label={label}
        helpText={helpText}
        helpTextPlacement={helpTextPlacement}
        required={required}
        wikiPopoverId={wikiPopoverId}
        wikiPopoverProps={wikiPopoverProps}
      />
      {renderInput ? renderInput(input) : input}
      {isInvalid && (
        <label className="label">
          <span className="text-error">
            <ErrorMessage name={field.name} />
          </span>
        </label>
      )}
    </div>
  );
};
